'use client';

import Icon from '@ant-design/icons';
import { generatePath } from '@remix-run/router';
import { Button, Modal, message } from 'antd';
import type { Options } from 'qr-code-styling';
import QRCodeStyling from 'qr-code-styling';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import DownloadSvg from '@/components/Icon/svgs/download1.svg';
import RightSvg from '@/components/Icon/svgs/right.svg';
import { useTranslation } from '@/i18n/client';
import { ROUTER_PATH } from '@/utils/router';

import styles from './index.less';

// 阻止事件冒泡到父组件
const stopPropagationModalRender = (node: ReactNode) => (
  <div className="w-full h-full" onClick={(e) => e.stopPropagation()}>
    {node}
  </div>
);

export interface IQRCodeModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  circleRoute: string;
  circleName?: string;
  circleIcon?: string;
}

const defaultImage =
  'https://mindverseglobal.mindverse.com/front-img/crm/1766814853894/defaultImage.png';

export const QRCodeModal = (props: IQRCodeModalProps): JSX.Element => {
  const { open, setOpen, circleRoute, circleName, circleIcon = defaultImage } = props;
  const { t } = useTranslation();

  const qrCodeRef = useRef<HTMLDivElement | null>(null);
  const [qrCode, setQrCode] = useState<QRCodeStyling>();

  // 关闭弹窗
  const handleClose = () => {
    setOpen(false);
  };

  // 下载二维码 - 使用高分辨率版本
  const handleDownload = () => {
    if (!circleRoute) return;

    // 创建高分辨率二维码用于下载 (1024x1024)
    const highResQrCode = new QRCodeStyling({
      width: 1024,
      height: 1024,
      type: 'canvas',
      data: `${window.origin}${generatePath(ROUTER_PATH.group, { route: circleRoute })}`,
      image: 'https://mindverseglobal.mindverse.com/front-img/crm/1761209164315/qrcode-img.png',
      margin: 40,
      qrOptions: {
        typeNumber: 0,
        mode: 'Byte',
        errorCorrectionLevel: 'H'
      },
      imageOptions: {
        hideBackgroundDots: false,
        imageSize: 0.3,
        margin: 0,
        crossOrigin: 'anonymous'
      },
      dotsOptions: {
        color: '#000000',
        type: 'dots'
      },
      cornersSquareOptions: {
        color: '#000000',
        type: 'extra-rounded'
      },
      cornersDotOptions: {
        color: '#000000',
        type: 'dot'
      },
      backgroundOptions: {
        color: '#ffffff'
      }
    });

    highResQrCode.download({
      name: `circle-qrCode-${circleRoute}`,
      extension: 'png'
    });
    message.success(t('common.downloadSuccessfully'));
  };

  // 二维码配置
  const qrCodeOptions: Options = useMemo(() => {
    return {
      width: 170,
      height: 170,
      type: 'svg',
      data: circleRoute
        ? `${window.origin}${generatePath(ROUTER_PATH.group, { route: circleRoute })}`
        : '',
      image: 'https://mindverseglobal.mindverse.com/front-img/crm/1761209164315/qrcode-img.png',
      margin: 0,
      qrOptions: {
        typeNumber: 0,
        mode: 'Byte',
        errorCorrectionLevel: 'H'
      },
      imageOptions: {
        hideBackgroundDots: false,
        imageSize: 0.3,
        margin: 0,
        crossOrigin: 'anonymous'
      },
      dotsOptions: {
        color: '#000000',
        type: 'dots'
      },
      cornersSquareOptions: {
        color: '#000000',
        type: 'extra-rounded'
      },
      cornersDotOptions: {
        color: '#000000',
        type: 'dot'
      },
      backgroundOptions: {
        color: 'transparent'
      }
    };
  }, [circleRoute]);

  // 初始化二维码
  useEffect(() => {
    if (open && circleRoute) {
      setQrCode(new QRCodeStyling(qrCodeOptions));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, circleRoute]);

  // 渲染二维码到DOM
  useEffect(() => {
    setTimeout(() => {
      if (qrCodeRef.current && qrCode) {
        qrCodeRef.current.innerHTML = '';
        qrCode.append(qrCodeRef.current);
      }
    }, 10);
  }, [qrCode]);

  // 更新二维码
  useEffect(() => {
    if (qrCode) {
      qrCode.update(qrCodeOptions);
    }
  }, [qrCode, qrCodeOptions]);

  return (
    <Modal
      centered
      className={styles.qrCodeModal}
      closable={false}
      destroyOnClose
      footer={null}
      maskClosable={true}
      modalRender={stopPropagationModalRender}
      onCancel={handleClose}
      open={open}
      width={580}
      zIndex={1100}
    >
      <div className={styles.qrCodePage}>
        {/* 头部 */}
        <div className={styles.qrCodeHeader}>
          <div className={styles.qrBackBtn} onClick={handleClose}>
            <Icon className={styles.qrBackIcon} component={RightSvg} />
          </div>
          <span className={styles.qrTitle}>{t('circle.qrCode')}</span>
          <div className={styles.placeholder} />
        </div>

        {/* 二维码内容区 */}
        <div className={styles.qrCodeContent}>
          <div className={styles.qrCodeInfo}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img alt="avatar" className={styles.qrCodeAvatar} src={circleIcon} />
            <span className={styles.qrCodeName}>{circleName}</span>
          </div>
          <div ref={qrCodeRef} className={styles.qrCodeImage} />
          <p className={styles.qrCodeTip}>{t('circle.scanToJoin')}</p>
        </div>

        {/* 下载按钮 */}
        <div className={styles.downloadContainer}>
          <Button className={styles.downloadBtn} onClick={handleDownload}>
            <Icon className={styles.downloadIcon} component={DownloadSvg} />
          </Button>
          <span className={styles.downloadText}>{t('common.download')}</span>
        </div>
      </div>
    </Modal>
  );
};

export default QRCodeModal;
